import { useNavigate } from 'react-router-dom'
import { useStripe } from '@stripe/react-stripe-js'
import { useQueryClient } from '@tanstack/react-query'

import { CreateTeamRequest } from '@pkg/types/api'

import api from '@/api'
import { TEAM_KEY } from '@/react-query/team'
import { DATABASE_USER_KEY } from '@/react-query/user'

/**
 * Creates the team and sends its new admin straight to Stripe checkout for
 * the same price as the personal plan the team creation cancels. The team
 * cache is only refreshed when checkout cannot start, so the dialog stays on
 * screen until the redirect instead of unmounting with the "no team" view.
 */
export const useCreateTeamAndCheckout = () => {
  const stripe = useStripe()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const redirectToTeamCheckout = async (lookupKey: string) => {
    if (!stripe) {
      return false
    }
    try {
      const { sessionId } = (await api.createCheckoutSession({ lookupKey }))
        .data
      const { error } = await stripe.redirectToCheckout({ sessionId })
      if (error) {
        console.error(error)
        return false
      }
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  return async (team: CreateTeamRequest, lookupKey: string | null) => {
    await api.createTeam(team)

    if (lookupKey && (await redirectToTeamCheckout(lookupKey))) {
      return
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [TEAM_KEY] }),
      queryClient.invalidateQueries({ queryKey: [DATABASE_USER_KEY] }),
    ])
    navigate('/pricing-page')
  }
}
